import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, ArrowLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { companyInfo } from '../data';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useDocumentTitle(service?.title || 'Ydelse');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then((res) => res.json())
      .then((data) => {
        // Find matching service by checking service_slug or slug.da or slug
        const matched = data.find(
          (s) =>
            s.service_slug === slug ||
            (s.slug && s.slug.da === slug) ||
            s.slug === slug
        );

        if (matched) {
          setService(matched);
          setOtherServices(data.filter((s) => s.id !== matched.id));
        } else {
          // Redirect to services if not found
          navigate('/ydelser');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching service details:', err);
        setLoading(false);
      });
  }, [slug, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          service_interest: service?.title || 'Generel forespørgsel'
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Jeg vender tilbage til dig hurtigst muligt, normalt inden for 24 timer.',
        });
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl. Prøv venligst igen eller ring til mig direkte på ' + companyInfo.phone,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-24 flex justify-center items-center">
        <div className="animate-pulse text-neutral-500 font-medium">Henter oplysninger...</div>
      </div>
    );
  }

  if (!service) return null;

  const imageUrl = Array.isArray(service.image) ? service.image[0] : service.image;

  return (
    <div className="w-full bg-neutral-50 min-h-screen pb-16">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column: Service Details */}
          <div className="lg:col-span-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-neutral-950 tracking-tight mb-6">
              {service.title}
            </h1>

            {/* Large Image */}
            <div className="relative h-[300px] md:h-[450px] overflow-hidden bg-neutral-100 rounded-sm mb-8 border border-neutral-200">
              <img
                src={imageUrl}
                alt={service.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Description (HTML) */}
            <div
              className="prose prose-neutral max-w-none text-neutral-700 leading-relaxed space-y-6"
              dangerouslySetInnerHTML={{ __html: service.description }}
            />
          </div>

          {/* Right Column: Sidebar Form & Contact */}
          <div className="lg:col-span-4 space-y-8">
            {/* Quick Contact Form */}
            <div className="bg-neutral-950 text-white p-6 md:p-8 rounded-sm shadow-md border border-neutral-800">
              <h3 className="text-lg font-bold mb-2">Få et uforpligtende tilbud</h3>
              <p className="text-neutral-400 text-xs leading-relaxed mb-6">
                Udfyld formularen, så kontakter jeg dig hurtigst muligt for at drøfte din opgave.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field - absolutely hidden from humans */}
                <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                  <label htmlFor="website_url">Do not fill this out</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-300 mb-1.5">
                    Navn *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-red-600"
                    placeholder="Dit fulde navn"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-300 mb-1.5">
                    Telefonnummer *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-red-600"
                    placeholder="F.eks. 12 34 56 78"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-300 mb-1.5">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-red-600"
                    placeholder="din@email.dk"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-300 mb-1.5">
                    Besked / Opgavebeskrivelse *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-red-600 resize-none"
                    placeholder="Fortæl kort om dit projekt..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-700 hover:bg-red-800 disabled:bg-neutral-800 text-white text-xs font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md shadow-sm"
                >
                  {submitting ? 'Sender...' : 'Send forespørgsel'}
                </button>

                {formStatus.message && (
                  <div
                    className={`p-3 rounded-sm text-xs font-medium ${
                      formStatus.type === 'success'
                        ? 'bg-emerald-950/50 border border-emerald-800 text-emerald-400'
                        : 'bg-red-950/50 border border-red-900 text-red-400'
                    }`}
                  >
                    {formStatus.message}
                  </div>
                )}
              </form>
            </div>

            {/* Direct Contact Info */}
            <div className="bg-white border border-neutral-200 p-6 md:p-8 rounded-sm shadow-sm">
              <h4 className="font-bold text-neutral-950 mb-4 border-b border-neutral-100 pb-2">
                Direkte kontakt
              </h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <Phone size={16} className="text-neutral-950 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-neutral-500">Ring til Christian</p>
                    <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="font-bold text-neutral-950 hover:text-red-700 transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={16} className="text-neutral-950 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-neutral-500">Send en e-mail</p>
                    <a href={`mailto:${companyInfo.email}`} className="font-bold text-neutral-950 hover:text-red-700 transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Related Services Section */}
        {otherServices.length > 0 && (
          <div className="mt-20 border-t border-neutral-200 pt-16">
            <h2 className="text-2xl font-extrabold text-neutral-950 tracking-tight mb-8">
              Andre ydelser jeg tilbyder
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherServices.slice(0, 3).map((s) => {
                const sImg = Array.isArray(s.image) ? s.image[0] : s.image;
                return (
                  <Link
                    key={s.id}
                    to={`/ydelser/${s.service_slug}`}
                    className="bg-white border border-neutral-200 rounded-sm overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col group"
                  >
                    <div className="relative h-48 overflow-hidden bg-neutral-100">
                      <img
                        src={sImg}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex-grow flex flex-col justify-between">
                      <div>
                        <h3 className="font-bold text-neutral-950 group-hover:text-red-700 transition-colors">
                          {s.title}
                        </h3>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-700 mt-4">
                        Læs mere <ChevronRight size={14} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
